// src/Dashboard/pages/user/Messaging/hooks/useMessagingDashboard.js

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { useUser } from "../../../../../pages/Home/UserContext";
import { getRoleFromRoleId } from "../../../../../utils/roleHelper";
import { useMessage } from "../../../../../hooks/MessageContext";

import {
  getMessagesByUser,
  getSentMessagesByUser,
  deleteMessageById,
  markMessageAsRead,
  markAllMessagesAsRead,
  sendMessage,
  sendGroupMessage,
} from "../../../../../services/messageServices";

import {
  getGroupMessages,
  deleteMessage as deleteChatMessage,
  createMessage as createChatMessage,
} from "../../../../../services/chatServices";

import { getGroupsList, getAllGroupsMessage } from "../../../../../services/groupServices";
import { getAdherentsByUser } from "../../../../../services/userServices";

import {
  getMessageDateValue,
  isUnreadMessage,
  sortByNewest,
} from "../utils/messagingUtils";

export function useMessagingDashboard() {
  const { user } = useUser();
  const userId = user?.userId;
  const userRole = getRoleFromRoleId(user?.role_id);
  const isManager = userRole === "admin" || userRole === "enseignant";

  const { setUnreadMessageCount } = useMessage();

  const [activeModule, setActiveModule] = useState("received");

  const [receivedMessages, setReceivedMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);

  const [loadingReceived, setLoadingReceived] = useState(false);
  const [loadingSent, setLoadingSent] = useState(false);
  const [busyAllRead, setBusyAllRead] = useState(false);

  const [composePrivateOpen, setComposePrivateOpen] = useState(false);
  const [composeGroupOpen, setComposeGroupOpen] = useState(false);

  const [readMessage, setReadMessage] = useState(null);
  const [readMessageType, setReadMessageType] = useState("received");

  const [replyToMessage, setReplyToMessage] = useState(null);

  const [channels, setChannels] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState(null);

  const [channelMessages, setChannelMessages] = useState([]);
  const [loadingChannelMessages, setLoadingChannelMessages] = useState(false);
  const [sendingChannelMessage, setSendingChannelMessage] = useState(false);

  const channelPollRef = useRef(null);

  const unreadCount = useMemo(() => {
    return receivedMessages.reduce(
      (sum, message) => sum + (isUnreadMessage(message) ? 1 : 0),
      0
    );
  }, [receivedMessages]);

  useEffect(() => {
    setUnreadMessageCount?.(unreadCount);
  }, [unreadCount, setUnreadMessageCount]);

  const loadReceivedMessages = useCallback(async () => {
    if (!userId) return;

    try {
      setLoadingReceived(true);

      const data = await getMessagesByUser(userId);
      const filtered = Array.isArray(data)
        ? data.filter((message) => !message.is_copy)
        : [];

      setReceivedMessages(sortByNewest(filtered));
    } catch (error) {
      console.error("Erreur chargement messages reçus :", error);
      toast.error("Erreur lors du chargement des messages reçus.");
    } finally {
      setLoadingReceived(false);
    }
  }, [userId]);

  const loadSentMessages = useCallback(async () => {
    if (!userId) return;

    try {
      setLoadingSent(true);

      const data = await getSentMessagesByUser(userId);
      const filtered = Array.isArray(data)
        ? data.filter((message) => message.is_copy)
        : [];

      setSentMessages(sortByNewest(filtered));
    } catch (error) {
      console.error("Erreur chargement messages envoyés :", error);
      toast.error("Erreur lors du chargement des messages envoyés.");
    } finally {
      setLoadingSent(false);
    }
  }, [userId]);

  const refreshPrivateMessages = useCallback(async () => {
    await Promise.all([loadReceivedMessages(), loadSentMessages()]);
  }, [loadReceivedMessages, loadSentMessages]);

  useEffect(() => {
    refreshPrivateMessages();
  }, [refreshPrivateMessages]);

  const openReadMessage = useCallback(
    async (message, type = "received") => {
      setReadMessage(message);
      setReadMessageType(type);

      if (type === "received" && isUnreadMessage(message)) {
        try {
          await markMessageAsRead(message.id);

          setReceivedMessages((previous) =>
            previous.map((item) =>
              item.id === message.id ? { ...item, read_message: true } : item
            )
          );
        } catch (error) {
          console.error("Erreur marquage message lu :", error);
        }
      }
    },
    []
  );

  const closeReadMessage = useCallback(() => {
    setReadMessage(null);
    setReadMessageType("received");
  }, []);

  const openReplyDrawer = useCallback((message) => {
    setReplyToMessage(message);
    setReadMessage(null);
  }, []);

  const closeReplyDrawer = useCallback(() => {
    setReplyToMessage(null);
  }, []);

  const deletePrivateMessage = useCallback(async (messageId) => {
    if (!messageId) return;

    const ok = window.confirm("Supprimer ce message ?");
    if (!ok) return;

    try {
      await deleteMessageById(messageId);

      setReceivedMessages((previous) =>
        previous.filter((message) => message.id !== messageId)
      );

      setSentMessages((previous) =>
        previous.filter((message) => message.id !== messageId)
      );

      setReadMessage((previous) =>
        previous?.id === messageId ? null : previous
      );

      toast.success("Message supprimé.");
    } catch (error) {
      console.error("Erreur suppression message :", error);
      toast.error("Erreur lors de la suppression du message.");
    }
  }, []);

  const markReceivedAsRead = useCallback(async (messageId) => {
    const target = receivedMessages.find((message) => message.id === messageId);
    if (!target || !isUnreadMessage(target)) return;

    try {
      await markMessageAsRead(messageId);

      setReceivedMessages((previous) =>
        previous.map((message) =>
          message.id === messageId ? { ...message, read_message: true } : message
        )
      );
    } catch (error) {
      console.error("Erreur marquage message lu :", error);
      toast.error("Échec du marquage en lu.");
    }
  }, [receivedMessages]);

  const markReceivedAsUnreadLocal = useCallback((messageId) => {
    setReceivedMessages((previous) =>
      previous.map((message) =>
        message.id === messageId ? { ...message, read_message: false } : message
      )
    );
  }, []);

  const handleMarkAllAsRead = useCallback(async () => {
    if (!userId || busyAllRead || receivedMessages.length === 0) return;

    try {
      setBusyAllRead(true);

      await markAllMessagesAsRead(userId);

      setReceivedMessages((previous) =>
        previous.map((message) => ({ ...message, read_message: true }))
      );

      toast.success("Tous les messages ont été marqués comme lus.");
    } catch (error) {
      console.error("Erreur marquage global lu :", error);
      toast.error("Échec du marquage global.");
    } finally {
      setBusyAllRead(false);
    }
  }, [busyAllRead, receivedMessages.length, userId]);

  const handlePrivateMessageSent = useCallback(async (payload) => {
    if (!userId) {
      toast.error("Utilisateur non identifié.");
      return;
    }

    try {
      await sendMessage({
        ...payload,
        sender_id: userId,
      });

      toast.success("Message envoyé avec succès.");

      setComposePrivateOpen(false);
      setReplyToMessage(null);

      await refreshPrivateMessages();
    } catch (error) {
      console.error("Erreur envoi message privé :", error);
      toast.error("Erreur lors de l'envoi du message.");
    }
  }, [refreshPrivateMessages, userId]);

  const handleGroupMessageSent = useCallback(async (payload) => {
    if (!userId) {
      toast.error("Utilisateur non identifié.");
      return;
    }

    try {
      await sendGroupMessage({
        ...payload,
        sender_id: userId,
      });

      toast.success("Message groupé envoyé avec succès.");
      setComposeGroupOpen(false);
    } catch (error) {
      console.error("Erreur envoi message groupé :", error);
      toast.error("Erreur lors de l'envoi du message groupé.");
    }
  }, [userId]);

  const loadChannels = useCallback(async () => {
    if (!userId) return;

    try {
      setLoadingChannels(true);

      if (isManager) {
        const list = await getGroupsList();
        const safe = Array.isArray(list) ? list : [];

        setChannels(safe);

        if (!selectedChannelId && safe.length > 0) {
          setSelectedChannelId(safe[0].id);
        }

        return;
      }

      const adherents = await getAdherentsByUser(userId);
      const unique = [];

      adherents?.forEach((adherent) => {
        adherent?.Groups?.forEach((group) => {
          if (!unique.find((item) => Number(item.id) === Number(group.id))) {
            unique.push({
              id: group.id,
              nom: group.nom,
              adherentNom: `${adherent.prenom || ""} ${adherent.nom || ""}`.trim(),
            });
          }
        });
      });

      setChannels(unique);

      if (!selectedChannelId && unique.length > 0) {
        setSelectedChannelId(unique[0].id);
      }
    } catch (error) {
      console.error("Erreur chargement canaux :", error);
      toast.error("Impossible de charger les canaux de discussion.");
    } finally {
      setLoadingChannels(false);
    }
  }, [isManager, selectedChannelId, userId]);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  const selectedChannel = useMemo(() => {
    return channels.find(
      (channel) => Number(channel.id) === Number(selectedChannelId)
    );
  }, [channels, selectedChannelId]);

  const loadChannelMessages = useCallback(
    async ({ silent = false } = {}) => {
      if (!selectedChannelId) return;

      try {
        if (!silent) {
          setLoadingChannelMessages(true);
        }

        const data = await getGroupMessages(selectedChannelId);
        const sorted = Array.isArray(data)
          ? [...data].sort(
              (a, b) =>
                new Date(getMessageDateValue(b) || 0).getTime() -
                new Date(getMessageDateValue(a) || 0).getTime()
            )
          : [];

        setChannelMessages(
          sorted.map((message) => ({
            ...message,
            currentUserId: userId,
          }))
        );
      } catch (error) {
        console.error("Erreur chargement messages canal :", error);
      } finally {
        if (!silent) {
          setLoadingChannelMessages(false);
        }
      }
    },
    [selectedChannelId, userId]
  );

  useEffect(() => {
    if (!selectedChannelId) return;

    loadChannelMessages();

    if (channelPollRef.current) {
      clearInterval(channelPollRef.current);
    }

    channelPollRef.current = setInterval(() => {
      loadChannelMessages({ silent: true });
    }, 5000);

    return () => {
      if (channelPollRef.current) {
        clearInterval(channelPollRef.current);
      }
    };
  }, [selectedChannelId, loadChannelMessages]);

  const sendChannelMessage = useCallback(
    async (text) => {
      const clean = String(text || "").trim();

      if (!selectedChannelId) {
        toast.warn("Sélectionnez un canal avant d’écrire.");
        return false;
      }

      if (!clean) return false;

      const temp = {
        id: `tmp-${Date.now()}`,
        user_id: userId,
        message: clean,
        createdAt: new Date().toISOString(),
        Users: {
          prenom: user?.prenom || "Moi",
          nom: user?.nom || "",
          photoURL: user?.photoURL || "",
        },
        currentUserId: userId,
        _optimistic: true,
      };

      try {
        setSendingChannelMessage(true);
        setChannelMessages((previous) => [temp, ...previous]);

        const saved = await createChatMessage({
          group_id: selectedChannelId,
          user_id: userId,
          message: clean,
        });

        if (saved?.id) {
          setChannelMessages((previous) =>
            previous.map((item) =>
              item.id === temp.id
                ? {
                    ...saved,
                    currentUserId: userId,
                  }
                : item
            )
          );
        }

        return true;
      } catch (error) {
        console.error("Erreur envoi message canal :", error);
        toast.error("Échec de l’envoi du message.");

        setChannelMessages((previous) =>
          previous.filter((message) => message.id !== temp.id)
        );

        return false;
      } finally {
        setSendingChannelMessage(false);
      }
    },
    [selectedChannelId, user, userId]
  );

  const deleteChannelMessage = useCallback(async (messageId) => {
    if (!messageId) return;

    const ok = window.confirm("Supprimer ce message du canal ?");
    if (!ok) return;

    try {
      await deleteChatMessage(messageId);

      setChannelMessages((previous) =>
        previous.filter((message) => message.id !== messageId)
      );

      toast.success("Message supprimé.");
    } catch (error) {
      console.error("Erreur suppression message canal :", error);
      toast.error("Impossible de supprimer ce message.");
    }
  }, []);

  return {
    user,
    userId,
    userRole,
    isManager,

    activeModule,
    setActiveModule,

    receivedMessages,
    sentMessages,
    loadingReceived,
    loadingSent,
    unreadCount,
    busyAllRead,

    composePrivateOpen,
    setComposePrivateOpen,
    composeGroupOpen,
    setComposeGroupOpen,

    readMessage,
    readMessageType,
    openReadMessage,
    closeReadMessage,

    replyToMessage,
    openReplyDrawer,
    closeReplyDrawer,

    deletePrivateMessage,
    markReceivedAsRead,
    markReceivedAsUnreadLocal,
    handleMarkAllAsRead,

    handlePrivateMessageSent,
    handleGroupMessageSent,

    channels,
    selectedChannel,
    selectedChannelId,
    setSelectedChannelId,
    loadingChannels,

    channelMessages,
    loadingChannelMessages,
    sendingChannelMessage,
    sendChannelMessage,
    deleteChannelMessage,
    refreshChannels: loadChannels,
    refreshChannelMessages: loadChannelMessages,
  };
}